import { useMutation, useQuery } from '@tanstack/react-query'
import { orgHref, siteCarriesTimes } from '../lib/public-router'
import { useTRPC } from '../lib/trpc'
import { Button, Notice, Select } from '../ui'
import './preferences.css'

type Channel = 'none' | 'email' | 'sms' | 'both'

/**
 * The self-service subscription page a member reaches from the link in a message
 * footer. No login: the `?token=` in the URL is a signed capability that scopes
 * every read/write to that one person. Billing is delivery-method-only — it has
 * no "Off", because dues notices are not optional.
 */
export function PreferencesPage() {
  const trpc = useTRPC()
  const token = new URLSearchParams(window.location.search).get('token') ?? ''

  const prefs = useQuery({
    ...trpc.preferences.get.queryOptions({ token }),
    enabled: token.length > 0,
    retry: false,
  })
  const setPref = useMutation({
    ...trpc.preferences.set.mutationOptions(),
    // The mutation returns the fresh, full preference state; refetch keeps the
    // page in step with the server (and any concurrent admin change).
    onSuccess: () => prefs.refetch(),
  })

  if (!token) {
    return (
      <Shell>
        <Notice tone="error">
          This link is missing its token. Please use the link from a message we
          sent you.
        </Notice>
      </Shell>
    )
  }
  if (prefs.isLoading) {
    return (
      <Shell>
        <p className="prefs__muted">Loading your preferences…</p>
      </Shell>
    )
  }
  if (prefs.isError || !prefs.data) {
    return (
      <Shell>
        <Notice tone="error">
          This link is invalid or has expired. Please use the link from a recent
          message, or contact{' '}
          {siteCarriesTimes() ? 'the shul office' : 'the office'}.
        </Notice>
      </Shell>
    )
  }

  return (
    <Shell subtitle={prefs.data.orgName}>
      <p className="prefs__hello">
        Preferences for <strong>{prefs.data.personName}</strong>
      </p>
      <p className="prefs__muted">
        Choose how you’d like to hear from us for each kind of message. Changes
        save immediately.
      </p>

      <ul className="prefs__list">
        {prefs.data.items.map((item) => (
          <li key={item.listId} className="prefs__row">
            <div className="prefs__rowText">
              <span className="prefs__rowLabel">{item.label}</span>
              {item.description && (
                <span className="prefs__rowBlurb">{item.description}</span>
              )}
            </div>
            <Select
              value={item.channel}
              disabled={setPref.isPending}
              onValueChange={(v) =>
                setPref.mutate({ token, listId: item.listId, channel: v })
              }
              options={[
                // A list the org does not allow muting offers no "off": a bill
                // is not a subscription. Which lists those are is the org's
                // answer, carried on the list itself.
                ...(item.mutable
                  ? [{ value: 'none' as Channel, label: 'Off' }]
                  : []),
                { value: 'email' as Channel, label: 'Email' },
                { value: 'sms' as Channel, label: 'Text message' },
                { value: 'both' as Channel, label: 'Email & text' },
              ]}
            />
          </li>
        ))}
      </ul>

      {setPref.isError && <Notice tone="error">{setPref.error.message}</Notice>}
      {setPref.isSuccess && !setPref.isPending && (
        <p className="prefs__saved">Saved.</p>
      )}

      <div className="prefs__foot">
        {/* Where a site carries the luach its schedule is what a member came
            for; elsewhere the site's own front door is. */}
        <Button
          variant="quiet"
          onClick={() =>
            window.location.assign(
              orgHref(
                prefs.data.orgShortId,
                siteCarriesTimes() ? 'zmanim' : '',
              ),
            )
          }
        >
          {siteCarriesTimes() ? 'Go to the zmanim page' : 'Go to the site'}
        </Button>
      </div>
    </Shell>
  )
}

function Shell({
  children,
  subtitle,
}: {
  children: React.ReactNode
  subtitle?: string
}) {
  return (
    <div className="prefs">
      <div className="prefs__card">
        <h1 className="prefs__title">Message preferences</h1>
        {subtitle && <p className="prefs__sub">{subtitle}</p>}
        {children}
      </div>
    </div>
  )
}
